import { CopyOutlined } from '@ant-design/icons';
import { Timestamp } from '@bufbuild/protobuf';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  notification,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import cn from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router';

import { OrganizationAPIKey } from 'protocol/api/auth/dto_auth_pb';
import { CalledVia } from 'protocol/model/dto_base_pb';

import { PATHS } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';

import { authModel } from '@/entities/auth';

import {
  FormValues,
  organizationApiKeysMessages as messages,
  viaLabels,
} from '../../config';

import styles from './OrganizationApiKeys.module.scss';

const { Title, Paragraph, Text } = Typography;

type Props = {
  organizationID: string;
  canCreate: boolean;
  canDelete: boolean;
  className?: string;
};

const isServiceCreator = (by: string) => by.toLowerCase().includes('diagnocat');

export const OrganizationApiKeys: FC<Props> = (props) => {
  const { organizationID, canCreate, canDelete, className } = props;

  const { formatMessage, formatDate } = useIntl();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<FormValues>();

  const [creating, setCreating] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState<OrganizationAPIKey[]>([]);
  const [createdKey, setCreatedKey] = useState<OrganizationAPIKey | null>(null);

  const fetchKeys = useCallback(async () => {
    setListLoading(true);
    try {
      const { APIKeys } = await dispatch(
        authModel.thunks.listOrganizationApiKeys({
          OrganizationID: organizationID,
        }),
      ).unwrap();
      setApiKeys(APIKeys);
    } catch (error) {
      notification.error({
        message:
          error instanceof Error
            ? error.message
            : formatMessage(messages.listError),
      });
    } finally {
      setListLoading(false);
    }
  }, [dispatch, organizationID, formatMessage]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async (values: FormValues) => {
    setCreating(true);
    try {
      const { APIKey } = await dispatch(
        authModel.thunks.createOrganizationApiKey({
          OrganizationID: organizationID,
          ExpiresAt: Timestamp.fromDate(values.expiresAt.toDate()),
          Comment: values.comment?.trim() || '',
        }),
      ).unwrap();

      setCreatedKey(APIKey ?? null);
      form.resetFields();
      notification.success({ message: formatMessage(messages.createSuccess) });
      await fetchKeys();
    } catch (error) {
      notification.error({
        message:
          error instanceof Error
            ? error.message
            : formatMessage(messages.createError),
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      notification.success({ message: formatMessage(messages.copySuccess) });
    } catch {
      notification.error({ message: formatMessage(messages.copyError) });
    }
  };

  const handleDelete = (apiKey: OrganizationAPIKey) => {
    Modal.confirm({
      title: formatMessage(messages.deleteConfirmTitle),
      content: formatMessage(messages.deleteConfirmContent),
      okText: formatMessage(messages.delete),
      okButtonProps: { danger: true },
      cancelText: formatMessage({
        id: 'global.cancel',
        defaultMessage: 'Cancel',
      }),
      onOk: async () => {
        try {
          await dispatch(
            authModel.thunks.deleteOrganizationApiKey({
              OrganizationID: organizationID,
              ID: apiKey.ID,
            }),
          ).unwrap();

          notification.success({
            message: formatMessage(messages.deleteSuccess),
          });
          if (createdKey?.ID === apiKey.ID) {
            setCreatedKey(null);
          }
          await fetchKeys();
        } catch (error) {
          notification.error({
            message:
              error instanceof Error
                ? error.message
                : formatMessage(messages.deleteError),
          });
        }
      },
    });
  };

  const handleCreateAnother = () => {
    setCreatedKey(null);
    form.resetFields();
  };

  const formatTimestamp = (timestamp?: Timestamp) => {
    if (!timestamp) {
      return '—';
    }

    return formatDate(timestamp.toDate(), {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderCreatedBy = (by?: string) => {
    if (!by) {
      return '—';
    }
    if (isServiceCreator(by)) {
      return by;
    }
    return <Link to={generatePath(PATHS.user, { userID: by })}>{by}</Link>;
  };

  const renderVia = (via?: CalledVia) => {
    const descriptor = via !== undefined ? viaLabels[via] : undefined;
    return descriptor ? formatMessage(descriptor) : '—';
  };

  const columns = [
    {
      title: formatMessage(messages.colId),
      dataIndex: 'ID',
      key: 'id',
      render: (id: string) => <Text copyable>{id}</Text>,
    },
    {
      title: formatMessage(messages.colCreatedBy),
      key: 'createdBy',
      render: (_: unknown, record: OrganizationAPIKey) =>
        renderCreatedBy(record.Created?.By),
    },
    {
      title: formatMessage(messages.colComment),
      key: 'comment',
      render: (_: unknown, record: OrganizationAPIKey) =>
        record.Created?.Comment || '—',
    },
    {
      title: formatMessage(messages.colCreatedAt),
      key: 'createdAt',
      render: (_: unknown, record: OrganizationAPIKey) =>
        formatTimestamp(record.Created?.At),
    },
    {
      title: formatMessage(messages.colExpiresAt),
      key: 'expiresAt',
      render: (_: unknown, record: OrganizationAPIKey) =>
        formatTimestamp(record.ExpiresAt),
    },
    {
      title: formatMessage(messages.colStatus),
      key: 'status',
      render: (_: unknown, record: OrganizationAPIKey) => {
        const expired =
          !!record.ExpiresAt &&
          dayjs(record.ExpiresAt.toDate()).isBefore(dayjs());
        return expired ? (
          <Tag color="default">{formatMessage(messages.statusExpired)}</Tag>
        ) : (
          <Tag color="green">{formatMessage(messages.statusActive)}</Tag>
        );
      },
    },
    ...(canDelete
      ? [
          {
            title: formatMessage(messages.colActions),
            key: 'actions',
            render: (_: unknown, record: OrganizationAPIKey) => (
              <Button danger size="small" onClick={() => handleDelete(record)}>
                {formatMessage(messages.delete)}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={cn(styles.container, className)}>
      <div>
        <Title level={4}>{formatMessage(messages.title)}</Title>
        <Paragraph type="secondary">
          {formatMessage(messages.description)}
        </Paragraph>
      </div>

      {!canCreate && !canDelete && (
        <Alert
          type="info"
          showIcon
          message={formatMessage(messages.viewOnlyNote)}
        />
      )}

      {canCreate && (
        <Form<FormValues>
          form={form}
          layout="vertical"
          className={styles.form}
          disabled={creating}
          initialValues={{ expiresAt: dayjs().add(1, 'year') }}
          onFinish={handleCreate}
        >
          <Form.Item
            name="expiresAt"
            label={formatMessage(messages.expiresAtLabel)}
            rules={[
              {
                required: true,
                message: formatMessage(messages.expiresAtRequired),
              },
              {
                validator: (_, value: Dayjs) => {
                  if (value && !value.isAfter(dayjs())) {
                    return Promise.reject(
                      new Error(formatMessage(messages.expiresAtInPast)),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              placeholder={formatMessage(messages.expiresAtPlaceholder)}
              disabledDate={(currentDate) =>
                !!currentDate &&
                currentDate.endOf('day').valueOf() < dayjs().valueOf()
              }
            />
          </Form.Item>

          <Form.Item
            name="comment"
            label={formatMessage(messages.commentLabel)}
          >
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 3 }}
              maxLength={500}
              placeholder={formatMessage(messages.commentPlaceholder)}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={creating}>
              {formatMessage(messages.submit)}
            </Button>
          </Form.Item>
        </Form>
      )}

      {createdKey && (
        <Card
          className={styles.result}
          title={formatMessage(messages.resultTitle)}
          bordered
        >
          <Alert
            type="warning"
            showIcon
            message={formatMessage(messages.resultWarning)}
          />

          <div>
            <Text type="secondary">{formatMessage(messages.secretLabel)}</Text>
            <div className={styles.secretRow}>
              <Input.TextArea
                className={styles.secretValue}
                value={createdKey.Secret}
                readOnly
                autoSize
              />
              <Button
                icon={<CopyOutlined rev={undefined} />}
                onClick={() => handleCopy(createdKey.Secret)}
              >
                {formatMessage(messages.copy)}
              </Button>
            </div>
          </div>

          <Descriptions column={1} size="small">
            <Descriptions.Item label={formatMessage(messages.keyIdLabel)}>
              <Text copyable>{createdKey.ID}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={formatMessage(messages.colCreatedBy)}>
              {renderCreatedBy(createdKey.Created?.By)}
            </Descriptions.Item>
            <Descriptions.Item
              label={formatMessage({
                id: 'global.created',
                defaultMessage: 'Created',
              })}
            >
              {formatTimestamp(createdKey.Created?.At)}
            </Descriptions.Item>
            <Descriptions.Item label={formatMessage(messages.colComment)}>
              {createdKey.Created?.Comment || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={formatMessage(messages.via)}>
              {renderVia(createdKey.Created?.Via)}
            </Descriptions.Item>
            <Descriptions.Item label={formatMessage(messages.expiresAtLabel)}>
              {formatTimestamp(createdKey.ExpiresAt)}
            </Descriptions.Item>
          </Descriptions>

          <Space>
            <Button onClick={handleCreateAnother}>
              {formatMessage(messages.createAnother)}
            </Button>
          </Space>
        </Card>
      )}

      <div>
        <Title level={5}>{formatMessage(messages.listTitle)}</Title>
        <Table<OrganizationAPIKey>
          rowKey="ID"
          size="small"
          loading={listLoading}
          columns={columns}
          dataSource={apiKeys}
          pagination={false}
        />
      </div>
    </div>
  );
};
